import { type ReactNode, type ElementType } from 'react';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  HStack,
  Heading,
  type AccordionProps,
  type AccordionPanelProps,
  type HStackProps,
} from '@frontend/legacy-components';

export type ViewerAccordionPanelProps = {
  title: string;
  icon?: ElementType;
  /** Theme color token for the title/icon, e.g. "alert.warning". */
  titleColor?: string;
  badgeCount?: number;
  badgeColorScheme?: string;
  defaultOpen?: boolean;
  /**
   * Space-scale width reserved on the right of the accordion button for the
   * headerAction overlay. Defaults to 4 (2rem — one icon button).
   */
  headerActionWidth?: number;
  /**
   * Optional control rendered at the right of the header (e.g. an "Edit"
   * button). Wrap with `ViewerAccordionPanelHeaderActionWrapper` so it sits as
   * a sibling of the accordion toggle — not nested inside it — and stays
   * independently clickable without nesting interactive elements.
   */
  headerAction?: ReactNode;
  children: ReactNode;
  /** Max height of the collapsible body. Unset sizes to content. */
  panelMaxH?: AccordionPanelProps['maxH'];
} & Omit<AccordionProps, 'children'>;

export const ViewerAccordionPanelHeaderActionWrapper = ({
  children,
  ...props
}: HStackProps) => (
  <HStack
    position="absolute"
    right={1.5}
    top={0}
    bottom={0}
    align="center"
    spacing={0}
    {...props}
  >
    {children}
  </HStack>
);
ViewerAccordionPanelHeaderActionWrapper.displayName =
  'ViewerAccordionPanelHeaderActionWrapper';

export const ViewerAccordionPanel = ({
  title,
  icon: Icon,
  titleColor = 'feature.primary',
  badgeCount,
  badgeColorScheme,
  defaultOpen = true,
  headerActionWidth = 4,
  headerAction,
  children,
  panelMaxH,
  sx,
  ...accordionProps
}: ViewerAccordionPanelProps) => (
  <Accordion
    allowMultiple={false}
    defaultIndex={defaultOpen ? 0 : -1}
    w="100%"
    pointerEvents="auto"
    display="flex"
    flexDirection="column"
    overflow="hidden"
    sx={{
      '& .chakra-collapse': {
        minH: 0,
        overflow: 'hidden',
      },
      ...sx,
    }}
    {...accordionProps}
  >
    <AccordionItem
      borderRadius="lg"
      boxShadow="popover"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      minH={0}
      flex="1"
      h="100%"
    >
      <Box position="relative" flexShrink={0}>
        <AccordionButton
          h="auto"
          py={1}
          px={1.5}
          pr={headerAction ? headerActionWidth : 1.5}
        >
          <HStack spacing={1} flex={1} minW={0} color={titleColor}>
            {Icon && <Icon boxSize={2.5} flexShrink={0} />}
            <Heading size="md" noOfLines={1} textAlign="left">
              {title}
            </Heading>
          </HStack>
          {badgeCount !== undefined && badgeCount > 0 && (
            <Badge
              colorScheme={badgeColorScheme}
              variant={badgeColorScheme ? 'subtle' : undefined}
            >
              {badgeCount}
            </Badge>
          )}
        </AccordionButton>
        {headerAction}
      </Box>
      <AccordionPanel
        maxH={panelMaxH}
        overflowY="auto"
        px={0}
        py={0}
        minH={0}
        flex="1"
        h="100%"
      >
        {children}
      </AccordionPanel>
    </AccordionItem>
  </Accordion>
);
ViewerAccordionPanel.displayName = 'ViewerAccordionPanel';
